import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Lock, Check, Gauge, Wrench, Plug, Hammer, Stethoscope, Bookmark,
  BookOpen, PiggyBank, ShieldCheck, CalendarDays, TrendingUp, FileText, Bell, Camera, Heart,
} from "lucide-react";
import { CapabilityGate } from "../features/experience";

// One consistent way to meet a Premium capability: explain what it is, why you'd
// want it and what you gain, then offer the upgrade. Never a wall, never a dead end.
export const PREMIUM_CONTENT = {
  "vehicle.health": {
    icon: Gauge, badge: "Vehicle Health", headline: "Understand your vehicle's story, not just its dates",
    body: "Vehicle Health turns years of MOT history into something you can actually use: plain-English explanations of advisories, recurring issues spotted before they become failures, and mileage trends over time.",
    points: [
      { icon: TrendingUp, title: "MOT insights, decoded", body: "Every advisory explained in plain English, so you know what matters before the garage does." },
      { icon: Bell, title: "Recurring issues spotted", body: "PlannioLife notices when the same problem keeps coming back across tests." },
      { icon: FileText, title: "A full health record", body: "Service history, costs and documents in one place for every vehicle you run." },
    ],
    examples: ["See whether that tyre advisory is new or a repeat offender", "Check mileage trends before selling or buying", "Keep a complete history to hand at the garage"],
  },
  "property.maintenance": {
    icon: Wrench, badge: "Home Maintenance", headline: "Upkeep that never slips through the cracks",
    body: "Maintenance turns good intentions into a gentle schedule: boiler services, gutter clears, alarm tests and seasonal jobs, each with reminders that arrive before the deadline does.",
    points: [
      { icon: CalendarDays, title: "A schedule that runs itself", body: "Set each job once and PlannioLife reminds you when it's due, year after year." },
      { icon: FileText, title: "A history worth keeping", body: "Every completed job logged, ready for insurers, buyers or your own records." },
      { icon: ShieldCheck, title: "Nothing forgotten", body: "Smoke alarms, boiler service, chimney sweep: the jobs that matter, remembered for you." },
    ],
    examples: ["Annual boiler service reminder with the engineer's details attached", "Proof of maintenance history when selling your home", "Seasonal jobs like gutter clearing, scheduled once and remembered forever"],
  },
  "property.utilities": {
    icon: Plug, badge: "Utilities & Renewals", headline: "Every supplier, tariff and renewal date in one view",
    body: "Keep electricity, gas, water, broadband and TV together with who supplies them, what you pay and when each deal ends, so renewal time never catches you out.",
    points: [
      { icon: Bell, title: "Renewals, remembered", body: "Know when each contract ends before the price jumps." },
      { icon: FileText, title: "One place for the details", body: "Account numbers and suppliers to hand when you need to call." },
      { icon: TrendingUp, title: "Switch with confidence", body: "Everything you need to compare deals, without digging through emails." },
    ],
    examples: ["Your broadband contract end date, a month before it renews", "Supplier and account number ready when the boiler engineer asks", "A clean handover when you move home"],
  },
  "property.trades": {
    icon: Hammer, badge: "Trusted Trades", headline: "The people who look after your home, on speed dial",
    body: "Your plumber, electrician, builder and gardener with notes on the work they've done, so the next job starts with a name you already trust.",
    points: [
      { icon: Check, title: "Never lose a good tradesperson", body: "Contact details and notes kept with the property they look after." },
      { icon: FileText, title: "Work history that stays", body: "What they did, when, and what it cost." },
      { icon: Heart, title: "Share with the household", body: "Everyone knows who to call when something goes wrong." },
    ],
    examples: ["The electrician who knows your fuse box, found in seconds", "Notes from the last boiler repair when the same fault returns", "A recommended roofer saved before you forget their name"],
  },
  "baby.health": {
    icon: Stethoscope, badge: "Health & Appointments", headline: "Every appointment, jab and milestone check, clearly organised",
    body: "From midwife visits to vaccination schedules, feeding logs to growth tracking: the health side of the journey, kept in one gentle place so you can be present for the rest.",
    points: [
      { icon: CalendarDays, title: "Appointments never missed", body: "Midwife, health visitor, dentist and check-ups, with reminders that arrive in time." },
      { icon: ShieldCheck, title: "Vaccinations on track", body: "The full schedule, marked off as you go." },
      { icon: TrendingUp, title: "Growth and routines", body: "Feeding, sleep and growth patterns when you want them, ignored when you don't." },
    ],
    examples: ["The 8-week jabs booked and remembered", "Hospital bag checklist ready before the big day", "A birth plan you can share with your midwife"],
  },
  "baby.keepsakes": {
    icon: Bookmark, badge: "Keepsakes & Memories", headline: "The moments you'll want back one day, kept safe",
    body: "First scan, first smile, first steps. The Keepsake Box gathers the photos, notes and memories you mark as special, ready to become a Journey Book you'll treasure.",
    points: [
      { icon: Camera, title: "A box for the precious things", body: "Mark any memory as a keepsake and it's gathered somewhere safe." },
      { icon: BookOpen, title: "A Journey Book to treasure", body: "Turn the journey into a beautiful book of chapters, milestones and memories." },
      { icon: Heart, title: "Yours forever", body: "Everything stays exactly as you left it, whatever plan you're on." },
    ],
    examples: ["The first scan photo, saved where it can't get lost", "A Journey Book of the whole pregnancy to look back on", "Grandparents' voices and messages kept alongside the milestones"],
  },
  "baby.finance": {
    icon: PiggyBank, badge: "Cost Planning", headline: "The costs of a growing family, without the spreadsheet",
    body: "From pram to nursery fees, plan what's coming with gentle cost tracking built for real family life, connected to your PlannioLife budgets.",
    points: [
      { icon: TrendingUp, title: "See what's ahead", body: "Plan the big purchases before they arrive." },
      { icon: PiggyBank, title: "Works with Budget", body: "Connect the journey to a budget and watch progress, not paperwork." },
      { icon: Check, title: "Decisions, not guesswork", body: "Know what you've spent and what's still to come." },
    ],
    examples: ["Nursery essentials planned and priced before the third trimester", "Childcare costs mapped out for the year ahead", "One clear view of what the first year really costs"],
  },
};

export function PremiumExperience({ capability, compact = false }) {
  const navigate = useNavigate();
  const c = PREMIUM_CONTENT[capability];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <div data-testid={`premium-experience-${capability.replace(".", "-")}`}
      className={`mx-auto ${compact ? "max-w-3xl" : "max-w-4xl"} px-1 py-4`}>
      <div className="flex flex-col items-start gap-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#0F766E]/10 px-3 py-1.5 text-xs font-semibold text-[#0F766E] dark:bg-[#0F766E]/20">
          <Icon className="h-4 w-4" /> {c.badge}
        </span>
        <h2 className="font-display text-3xl font-semibold text-[#2D3436] dark:text-white sm:text-4xl">{c.headline}</h2>
        <p className="max-w-2xl text-base leading-relaxed text-[#636E72] dark:text-gray-300">{c.body}</p>
        <div className="mt-1 flex flex-wrap gap-3">
          <button data-testid="premium-experience-upgrade" onClick={() => navigate("/app/billing?plan=premium")}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0F766E] px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5">
            Upgrade to Premium <ArrowRight className="h-4 w-4" />
          </button>
          <span className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm text-[#636E72] dark:border-white/10 dark:text-gray-300">
            <Lock className="h-4 w-4 text-[#B45309]" /> Available on Premium &amp; Family
          </span>
        </div>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {c.points.map((p) => (
          <div key={p.title} className="rounded-2xl border border-[#EBECE8] bg-white p-5 dark:border-white/10 dark:bg-white/5" data-testid="premium-experience-point">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0F766E]/10 dark:bg-[#0F766E]/20">
              <p.icon className="h-5 w-5 text-[#0F766E]" strokeWidth={1.6} />
            </span>
            <h3 className="mt-3 font-display text-base font-medium text-[#2D3436] dark:text-white">{p.title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-[#636E72] dark:text-gray-300">{p.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-2xl border border-[#EBECE8] bg-white p-6 dark:border-white/10 dark:bg-white/5">
        <h3 className="font-display text-base font-medium text-[#2D3436] dark:text-white">A few ways people use it</h3>
        <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
          {c.examples.map((e) => (
            <li key={e} className="flex items-start gap-2.5 text-sm text-[#636E72] dark:text-gray-300">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F766E]" /> {e}
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-4 text-xs text-[#9aa3a0]">Everything you have already added stays yours, always, whatever plan you're on.</p>
    </div>
  );
}

// Route or panel level gate: children when the capability is available, the
// explanation experience when it isn't.
export function PremiumGate({ capability, compact = false, children }) {
  return (
    <CapabilityGate capability={capability} fallback={<PremiumExperience capability={capability} compact={compact} />}>
      {children}
    </CapabilityGate>
  );
}
